import type { ReactNode } from "react";
import cx from "classnames";

import { Icon } from "common/ui/Icon";
import { Button } from "common/ui/Button";
import { Stack } from "common/ui/Stack";
import { Heading } from "common/ui/Heading";
import { Paragraph } from "common/ui/Paragraph";
import { ErrorDisplay } from "common/ui/ErrorDisplay";

import styles from "./Banner.module.scss";

export type BannerVariants =
  "default"
  | "informational"
  | "critical"
  | "success"
  | "warning"

interface Props {
  children: ReactNode,
  heading?: string,
  primaryAction?: { text: string, onClick:() => void },
  secondaryAction?: { text: string, onClick:() => void },
  isProcessing?: boolean;
  variant?: BannerVariants,
  alertIcon?: boolean,
  close?: () => any;
  error?: string,
  className?: string,
}

export function Banner({
  children,
  heading,
  primaryAction,
  secondaryAction,
  isProcessing,
  variant = "default",
  alertIcon = true,
  close,
  error,
  className,
}: Props) {
  return (
    <div className={cx(styles.Banner, className, {
      [styles.default]: variant === "default",
      [styles.informational]: variant === "informational",
      [styles.critical]: variant === "critical",
      [styles.success]: variant === "success",
      [styles.warning]: variant=== "warning",
    })}>
      {alertIcon && <Icon icon="alertMajor"/>}
      <div className={styles.Content}>
        <Stack direction="column" gap={4}>
          {heading && <Heading variant="h3">{heading}</Heading>}
          {(typeof children === "string" || typeof children === "number") ? (
            <Paragraph variant="p1">{children}</Paragraph>
          ) : (
            children
          )}
        </Stack>
        {(primaryAction || secondaryAction) && (
          <Stack direction="column" gap={4}>
            <div className={styles.Button}>
              {secondaryAction && (
                <Button
                  disabled={isProcessing}
                  variant="secondary"
                  onClick={secondaryAction.onClick}
                >
                  {secondaryAction.text}
                </Button>
              )}
              {primaryAction && (
                <Button
                  disabled={isProcessing}
                  variant="tertiary"
                  onClick={primaryAction.onClick}
                >
                  {primaryAction.text}
                </Button>
              )}
            </div>
            {error && <ErrorDisplay error={error} />}
          </Stack>
        )}
      </div>
      {close && (
        <Stack alignItems="center">
          <Icon
            icon="closeMinor"
            onClick={() => close()}
          />
        </Stack>
      )}
    </div>
  )
}

